import { createSelector } from 'reselect'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { decodeSearchParams } from 'lib/search/searchUtils'
import { isString, slugify, isSlug } from 'lib/string/stringUtils'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { LOYALTY_SEARCH_FILTER_PARAM } from 'loyalty/constants/loyaltySearchFilters'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { chainsDelimiter, subChainsDelimiter } from 'constants/url'

function normaliseFilterSlugs(values: Array<string>): Array<string> {
  return values.map(v => isSlug(v) ? v : slugify(v))
}

export const getUrlOfferListFilters = createSelector(
  (state: App.State) => state.router.location.search,
  (search): App.OfferListFilters => {
    const params = parseSearchString(search)
    const searchParams = decodeSearchParams(search)

    return {
      locations: params.locations ? params.locations.split(',') : [],
      holidayTypes: params.holidayTypes ? normaliseFilterSlugs(params.holidayTypes.split(',')) : [],
      holidayTypesScoped: params.holidayTypesScoped ? normaliseFilterSlugs(params.holidayTypesScoped.split(',')) : [],
      propertyTypes: params.propertyTypes ? normaliseFilterSlugs(params.propertyTypes.split(',')) : [],
      campaigns: params.campaigns ? params.campaigns.split(',') : [],
      sortBy: params.sortBy as App.OfferListSortByOptions,
      strategyApplied: params.strategyApplied,
      destinationId: params.destinationId?.split(',')[0], // First destination ID (single)
      destinationIds: params.destinationId ? params.destinationId.split(',').filter(Boolean) : undefined, // All destination IDs (array)
      landmarkId: params.landmarkId?.split(',')[0], // First landmark ID (single)
      landmarkIds: params.landmarkId ? params.landmarkId.split(',').filter(Boolean) : undefined, // All landmark IDs (array)
      propertyId: params.propertyId,
      bounds: params.bounds,
      checkIn: searchParams.dates.checkIn,
      checkOut: searchParams.dates.checkOut,
      roomRateId: searchParams.roomRateId,
      roomTypeId: searchParams.roomTypeId,
      flexibleMonths: searchParams.flexibleSearch.flexibleMonths,
      flexibleNights: searchParams.flexibleSearch.flexibleNights,
      userSelectedFlexibleMonths: searchParams.flexibleSearch.userSelectedFlexibleMonths,
      rooms: searchParams.rooms,
      amenities: params.amenities ? normaliseFilterSlugs(params.amenities.split(',')) : [],
      inclusions: params.inclusions ? normaliseFilterSlugs(params.inclusions.split(',')) : [],
      inclusionGap: params.inclusionGap || undefined,
      tags: params.tags ? params.tags.split(',') : [],
      offerTags: params.offerTags ? params.offerTags.split(',') : [],
      limit: params.limit ? parseInt(params.limit, 10) : undefined,
      customerRatingGte: searchParams.customerRatingGte ? Number(searchParams.customerRatingGte) : undefined,
      priceGte: params.priceGte,
      priceLte: params.priceLte,
      operators: params.operators ? params.operators.split(',') : [],
      tourLengthGte: params.tourLengthGte,
      tourLengthLte: params.tourLengthLte,
      destinations: params.destinations && isString(params.destinations) ?
          params.destinations.split(',') :
        params.destinations && Array.isArray(params.destinations) ?
          params.destinations : [],
      // 'categories' is also used for experience lists and they are an -array- of string instead
      // this one is used for tour categories
      categories: typeof params.categories === 'string' ? params.categories.split(',') : [],
      offerTypes: searchParams.offerType ? searchParams.offerType.split(',') as Array<App.OfferListOfferType> : undefined,
      // @ts-expect-error
      productTypes: params.productTypes,
      bedroomsGte: params.bedroomsGte ? parseInt(params.bedroomsGte) : undefined,
      bedroomsEq: params.bedroomsEq ? parseInt(params.bedroomsEq) : undefined,
      bedsGte: params.bedsGte ? parseInt(params.bedsGte) : undefined,
      bedsEq: params.bedsEq ? parseInt(params.bedsEq) : undefined,
      bathroomsGte: params.bathroomsGte ? parseInt(params.bathroomsGte) : undefined,
      bathroomsEq: params.bathroomsEq ? parseInt(params.bathroomsEq) : undefined,
      onSale: params.onSale === 'true' ? true : undefined,
      flashOffer: params.flashOffer === 'true' ? true : undefined,
      viewPriceInPoints: params.viewPriceInPoints === 'true' ? true : undefined,
      luxPlusFeatures: params.luxPlusFeatures?.split(',') as Array<App.LuxPlusFeatureFilter> ?? [],
      agentHubFeatures: params.agentHubFeatures?.split(',') as Array<App.AgentHubFeatureFilter> ?? [],
      tourStyles: params.tourStyles?.split(',') ?? EmptyArray,
      source: params.source,
      loyalty: params[LOYALTY_SEARCH_FILTER_PARAM]?.split(','),
      paymentOptions: params.paymentOptions ? normaliseFilterSlugs(params.paymentOptions.split(',')) : undefined,
      chains: params.chains?.split(chainsDelimiter),
      subChains: params.subChains?.split(subChainsDelimiter),
      lat: params.lat ? parseFloat(params.lat) : undefined,
      lng: params.lng ? parseFloat(params.lng) : undefined,
      query: params.query ?? params.destinationName ?? params.landmarkName ?? params.propertyName,
    }
  },
)

// GHA (Google Hotel Ads) traffic from the main metasearch offer sets gha_price=1
// to show B2C price prominently, matching the Google-advertised price.
// Scoped to partner pages only.
export const isGhaPriceDisplay = createSelector(
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.router.location.pathname,
  (search, pathname) => pathname.includes('/partner/') && new URLSearchParams(search).get('gha_price') === '1',
)

/**
 * Check if current route is a specific campaign page
 * Memoized selector that takes specificCampaign as a parameter
 *
 * @param state App.State
 * @param specificCampaign string - the campaign name to check
 * @returns boolean
 */
export const getIsSpecificCampaignPage = createSelector(
  (state: App.State) => state.router.location.pathname,
  (_state: App.State, specificCampaign: string) => specificCampaign,
  (pathname, specificCampaign) => {
    const isCampaignPage = matchLocaleAwarePath(pathname, { path: '/:regionCode/campaign', exact: false })
    return isCampaignPage ? last(pathname.split('/')) === specificCampaign : false
  },
)
